import React, { useState, useEffect, useMemo, useRef } from 'react';
import { withAuthenticator } from '@aws-amplify/ui-react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  Slide,
  ToggleButtonGroup,
  ToggleButton,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  FilterAlt as FilterIcon,
  AutoAwesome as AutoAwesomeIcon,
  FormatListBulleted as ListIcon
} from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HelpIcon from '@mui/icons-material/Help';
import axios from 'axios';
import { useGamesCache } from '../contexts/GamesContext';
import CreateGameModal from '../components/CreateGameModal';
import ManageListsModal from '../components/ManageListsModal';
import SightingCropImage from '../components/SightingCropImage';
import moment from 'moment';

const AnomalyReview = () => {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [listData, setListData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filterTags, setFilterTags] = useState([]);
  const [tagFilterMode, setTagFilterMode] = useState('ALL');

  // Modals
  const [manageListsOpen, setManageListsOpen] = useState(false);

  const [suggestions, setSuggestions] = useState({}); // gameId -> suggestion
  const { gameMap: gamesMap, fullGamesMap, fetchGames, studios: studiosCatalog, fetchStudios } = useGamesCache();
  const [operatorsCatalog, setOperatorsCatalog] = useState([]);

  // What's New Modal State
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewLang, setWhatsNewLang] = useState('en');

  useEffect(() => {
    const seen = localStorage.getItem('whats_new_seen_anomaly_review_v1');
    if (!seen) {
      setWhatsNewOpen(true);
    }
  }, []);

  const handleCloseWhatsNew = () => setWhatsNewOpen(false);
  const handleDontShowWhatsNew = () => {
    localStorage.setItem('whats_new_seen_anomaly_review_v1', 'true');
    setWhatsNewOpen(false);
  };

  // Search Controls
  const [startDate, setStartDate] = useState(moment.utc().startOf('week').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment.utc().startOf('week').add(6, 'days').format('YYYY-MM-DD'));

  const handleStartDateChange = (newStart) => {
    setStartDate(newStart);
    if (newStart) {
      setEndDate(moment.utc(newStart).add(6, 'days').format('YYYY-MM-DD'));
    }
  };

  const handleShiftDays = (days) => {
    setStartDate(prev => moment.utc(prev).add(days, 'days').format('YYYY-MM-DD'));
    setEndDate(prev => moment.utc(prev).add(days, 'days').format('YYYY-MM-DD'));
  };
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [websitesCatalog, setWebsitesCatalog] = useState({});
  const [availableTags, setAvailableTags] = useState(['all']);

  const [auditSummaryWebsites, setAuditSummaryWebsites] = useState([]);

  // Actions State
  const [tileMetadata, setTileMetadata] = useState({}); // { [tile.BoundingBoxID]: { WebsiteName, Timestamp, URL, WebsitesDBID } }
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [tilesToReassign, setTilesToReassign] = useState([]);
  const [selectedNewGame, setSelectedNewGame] = useState(null);
  const [currentAnomalyIndex, setCurrentAnomalyIndex] = useState(0);
  const [batchLoading, setBatchLoading] = useState(false);

  const carouselRef = useRef(null);
  const handleWheel = React.useCallback((e) => {
    if (e.deltaY !== 0) {
      e.preventDefault();
      e.currentTarget.scrollLeft += e.deltaY;
    }
  }, []);

  const setCarouselRef = React.useCallback((element) => {
    if (carouselRef.current) {
      carouselRef.current.removeEventListener('wheel', handleWheel);
    }
    carouselRef.current = element;
    if (element) {
      element.addEventListener('wheel', handleWheel, { passive: false });
    }
  }, [handleWheel]);

  useEffect(() => {
    fetchLists();

    const fetchCatalog = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/websites/catalog`, {
          headers: { 'x-api-key': process.env.REACT_APP_API_KEY }
        });
        const sites = Array.isArray(res.data) ? res.data : res.data.websites || [];
        const catalog = {};
        const t = new Set();
        sites.forEach(w => {
          catalog[w.WebsitesDBID] = w;
          w.Tags?.forEach(tag => t.add(tag));
        });
        setWebsitesCatalog(catalog);
        setAvailableTags(['all', ...Array.from(t).sort()]);
      } catch (e) {
        console.warn('Failed to fetch catalog', e);
      }
    };
    fetchCatalog();

    fetchGames();
    fetchStudios();

    const fetchOperators = async () => {
      try {
        const operatorsRes = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/operators`, {
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
        });
        setOperatorsCatalog(operatorsRes.data.operators || []);
      } catch (e) {
        console.warn('Failed to fetch operators', e);
      }
    };
    fetchOperators();
  }, [fetchGames, fetchStudios]);



  const fetchLists = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/lists`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
      });

      const smartInhouseList = {
        ListID: 'smart_inhouse',
        Name: 'Inhouse Operator Anomalies',
        Type: 'SmartInhouse'
      };
      setLists([smartInhouseList, ...res.data]);
      if (res.data && res.data.length > 0 && !selectedList) {
        setSelectedList(res.data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch lists', err);
      setError('Failed to fetch anomaly lists.');
    } finally {
      setLoading(false);
    }
  };



  const currentFetchListId = useRef(null);

  const fetchTiles = async (listId, start, end) => {
    currentFetchListId.current = listId;
    try {
      setLoading(true);
      setListData(null);
      let url = `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/tiles?listId=${listId}&startDate=${start}&endDate=${end}`;

      const res = await axios.get(url, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
      });


      if (currentFetchListId.current !== listId) return; // Stale response, ignore


      const missingGames = res.data.Games.filter(gId => !gamesMap[gId]);

      if (missingGames.length > 0) {
        await fetchGames(true);
      }

      // Pre-fetch all website metadata for the loaded tiles
      const uniqueWdIds = new Set();
      if (res.data && res.data.Tiles) {
        Object.values(res.data.Tiles).forEach(tiles => {
          tiles.forEach(t => {
            if (t.WebsitesDataID) uniqueWdIds.add(t.WebsitesDataID);
          });
        });
      }

      const idsToFetch = Array.from(uniqueWdIds).filter(id => !tileMetadata[id]);
      if (idsToFetch.length > 0) {
        try {
          const batchPromises = idsToFetch.map(id =>
            axios.get(`${process.env.REACT_APP_API_URL}/screenshots/${id}`, { headers: { 'x-api-key': process.env.REACT_APP_API_KEY } })
              .catch(e => null)
          );
          const batchRes = await Promise.all(batchPromises);
          setTileMetadata(prev => {
            const updated = { ...prev };
            batchRes.forEach((r, idx) => {
              if (r && r.data) {
                updated[idsToFetch[idx]] = r.data;
              } else {
                updated[idsToFetch[idx]] = { WebsitesDBID: 'unknown', WebsiteName: 'Unknown' };
              }
            });
            return updated;
          });
        } catch (e) {
          console.error("Batch metadata fetch failed", e);
        }
      }


      // Fetch suggestions for each game
      const newSuggestions = {};
      await Promise.all(
        res.data.Games.map(async (gameId) => {
          try {
            const sugRes = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/suggestions/${gameId}`, {
              headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
            });
            if (sugRes.data.Suggestions && sugRes.data.Suggestions.length > 0) {
              newSuggestions[gameId] = sugRes.data.Suggestions[0];
            }
          } catch (e) {
            // Ignore errors for individual suggestions
          }
        })
      );
      setSuggestions(newSuggestions);

      setListData(res.data);

    } catch (err) {
      console.error('Failed to fetch tiles', err);
      setError('Failed to fetch tiles for selected list.');
    } finally {
      setLoading(false);
    }
  };



  const fetchAuditSummary = async (start, end) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/audit/summary`, {
        params: {
          startDate: moment.utc(start).startOf('day').toISOString(),
          endDate: moment.utc(end).endOf('day').toISOString()
        },
        headers: { 'x-api-key': process.env.REACT_APP_API_KEY }
      });
      if (res.data && res.data.websites) {
        setAuditSummaryWebsites(res.data.websites);
      } else {
        setAuditSummaryWebsites([]);
      }
    } catch (e) {
      console.warn('Failed to fetch audit summary', e);
      setAuditSummaryWebsites([]);
    }
  };

  useEffect(() => {
    if (selectedList && startDate && endDate) {
      fetchTiles(selectedList.ListID, startDate, endDate);
      fetchAuditSummary(startDate, endDate);
    } else {
      setListData(null);
      setAuditSummaryWebsites([]);
    }
  }, [selectedList, startDate, endDate]);



  const handleApplyCorrection = async (originalGameId, correctedGameId, tiles) => {
    if (!window.confirm(`Are you sure you want to apply this correction to all ${tiles.length} tiles?`)) return;
    setBatchLoading(true);
    try {
      const correctedGameName = gamesMap[correctedGameId] || correctedGameId;

      // Log the correction
      await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/anomaly-review/corrections`,
        {
          OriginalGameID: originalGameId,
          CorrectedGameID: correctedGameId,
          Source: 'AnomalyReview'
        },
        { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
      );

      // Process the reassignment for all tiles
      const promises = tiles.map(async (tile) => {
        await axios.post(`${process.env.REACT_APP_API_ANALYST_URL}/set-unconfirmed-analysis`, {
          WebsitesDataID: tile.WebsitesDataID,
          BoundingBoxID: tile.BoundingBoxID,
          GameID: correctedGameId,
          BoxType: 'Game',
          Status: 'COMPLETE',
          stage: 'ManualMatch',
          RemoveAttributes: ['DescriptiveText', 'AssociatedGames', 'GroupName', 'ChildBoundingBoxIDs']
        }, { headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY, 'Content-Type': 'application/json' } });

        const meta = tileMetadata[tile.WebsitesDataID] || {};
        const llmMatchBody = {
          DetectedText: tile.DetectedText || correctedGameName,
          GameID: correctedGameId,
          GameName: correctedGameName,
          MatchType: 'Manual',
          BoundingBoxID: tile.BoundingBoxID,
          WebsiteName: meta.WebsiteName || 'Unknown',
          URL: meta.URL || 'Unknown'
        };
        await axios.post(`${process.env.REACT_APP_API_ANALYST_URL}/llm-text-match`, llmMatchBody, {
          headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY }
        });
      });
      await Promise.all(promises);

      // Update local state to remove reassigned tiles
      setListData(prev => {
        const newTiles = { ...prev.Tiles };
        if (newTiles[originalGameId]) {
          newTiles[originalGameId] = newTiles[originalGameId].filter(
            t => !tiles.find(dt => dt.BoundingBoxID === t.BoundingBoxID)
          );
        }
        return { ...prev, Tiles: newTiles };
      });

      alert('Correction applied and logged!');
    } catch (err) {
      console.error('Failed to apply correction', err);
      alert('Failed to apply correction completely. Please try again.');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDeleteTile = async (tile, gameId) => {
    if (!window.confirm('Are you sure you want to delete this occurrence?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_ANALYST_URL}/delete-unconfirmed-analysis`, {
        data: { WebsitesDataID: tile.WebsitesDataID, BoundingBoxID: tile.BoundingBoxID },
        headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY }
      });
      setListData(prev => ({
        ...prev,
        Tiles: {
          ...prev.Tiles,
          [gameId]: prev.Tiles[gameId].filter(t => t.BoundingBoxID !== tile.BoundingBoxID)
        }
      }));
    } catch (err) {
      console.error('Failed to delete tile', err);
      alert('Failed to delete tile');
    }
  };

  const handleReassignTile = async () => {
    if (tilesToReassign.length === 0 || !selectedNewGame) return;
    setBatchLoading(true);
    try {
      const promises = tilesToReassign.map(async ({ tile, gameId }) => {
        await axios.post(`${process.env.REACT_APP_API_ANALYST_URL}/set-unconfirmed-analysis`, {
          WebsitesDataID: tile.WebsitesDataID,
          BoundingBoxID: tile.BoundingBoxID,
          GameID: selectedNewGame.id,
          BoxType: 'Game',
          Status: 'COMPLETE',
          stage: 'ManualMatch',
          RemoveAttributes: ['DescriptiveText', 'AssociatedGames', 'GroupName', 'ChildBoundingBoxIDs']
        }, { headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY, 'Content-Type': 'application/json' } });

        const meta = tileMetadata[tile.WebsitesDataID] || {};
        const llmMatchBody = {
          DetectedText: tile.DetectedText || selectedNewGame.name,
          GameID: selectedNewGame.id,
          GameName: selectedNewGame.name,
          MatchType: 'Manual',
          BoundingBoxID: tile.BoundingBoxID,
          WebsiteName: meta.WebsiteName || 'Unknown',
          URL: meta.URL || 'Unknown'
        };
        await axios.post(`${process.env.REACT_APP_API_ANALYST_URL}/llm-text-match`, llmMatchBody, {
          headers: { 'x-api-key': process.env.REACT_APP_API_ANALYST_KEY }
        });
      });
      await Promise.all(promises);

      setListData(prev => {
        const newTiles = { ...prev.Tiles };
        tilesToReassign.forEach(({ tile, gameId }) => {
          if (newTiles[gameId]) {
            newTiles[gameId] = newTiles[gameId].filter(t => t.BoundingBoxID !== tile.BoundingBoxID);
          }
        });
        return { ...prev, Tiles: newTiles };
      });
      setReassignModalOpen(false);
      setTilesToReassign([]);
      setSelectedNewGame(null);
    } catch (err) {
      console.error('Failed to reassign tiles', err);
      alert('Failed to reassign some tiles');
    } finally {
      setBatchLoading(false);
    }
  };

  const regions = useMemo(() => {
    const r = new Set();
    Object.values(websitesCatalog).forEach(w => {
      if (w.Region) r.add(w.Region);
    });
    return ['all', ...Array.from(r).sort()];
  }, [websitesCatalog]);

  const countries = useMemo(() => {
    const c = new Set();
    Object.values(websitesCatalog).forEach(w => {
      if (selectedRegion === 'all' || w.Region === selectedRegion) {
        if (w.Country) c.add(w.Country);
      }
    });
    return ['all', ...Array.from(c).sort()];
  }, [websitesCatalog, selectedRegion]);

  const studiosMap = useMemo(() => {
    const m = {};
    studiosCatalog.forEach(s => m[s.StudioID] = s);
    return m;
  }, [studiosCatalog]);

  const operatorsMap = useMemo(() => {
    const m = {};
    operatorsCatalog.forEach(o => m[o.OperatorID] = o.OperatorName);
    return m;
  }, [operatorsCatalog]);

  const filteredTiles = useMemo(() => {
    if (!listData || !listData.Tiles) return {};
    const filtered = {};
    Object.keys(listData.Tiles).forEach(gameId => {
      const tiles = listData.Tiles[gameId] || [];
      filtered[gameId] = tiles.filter(tile => {
        const meta = tileMetadata[tile.WebsitesDataID];
        if (!meta) return false; // Hide until metadata finishes pre-loading
        const website = websitesCatalog[meta.WebsitesDBID];
        if (!website) return true; // Show if website is not in catalog for some reason

        if (selectedList?.ListID === 'smart_inhouse') {
          const game = fullGamesMap[gameId];
          if (!game) return false;
          const studio = studiosMap[game.StudioID];
          if (!studio || !studio.OperatorID) return false; // not an inhouse game
          if (website.OperatorID === studio.OperatorID) return false; // matches, so not an anomaly
        }

        // Filter by Region
        if (selectedRegion !== 'all' && website.Region !== selectedRegion) {
          return false;
        }
        // Filter by Country
        if (selectedCountry !== 'all' && website.Country !== selectedCountry) {
          return false;
        }
        // Filter by Tag
        if (selectedTag !== 'all' && (!website.Tags || !website.Tags.includes(selectedTag))) {
          return false;
        }
        return true;
      });
    });
    return filtered;
  }, [listData, tileMetadata, websitesCatalog, selectedRegion, selectedCountry, selectedTag, selectedList, fullGamesMap, studiosMap]);

  const anomalyGames = useMemo(() => {
    return Object.keys(filteredTiles).filter(gameId => filteredTiles[gameId].length > 0);
  }, [filteredTiles]);

  useEffect(() => {
    setCurrentAnomalyIndex(0);
  }, [selectedRegion, selectedCountry, selectedTag, selectedList, startDate, endDate]);

  const totalTilesCount = useMemo(() => {
    if (!listData || !listData.Tiles) return 0;
    let count = 0;
    Object.values(listData.Tiles).forEach(tiles => {
      count += tiles.length;
    });
    return count;
  }, [listData]);

  const possibleAnomaliesCount = useMemo(() => {
    let count = 0;
    Object.values(filteredTiles).forEach(tiles => {
      count += tiles.length;
    });
    return filteredTiles && Object.values(filteredTiles).reduce((sum, tiles) => sum + tiles.length, 0);
  }, [filteredTiles]);

  const totalScannedTiles = useMemo(() => {
    let sum = 0;
    auditSummaryWebsites.forEach(site => {
      if (selectedRegion !== 'all' && site.Region !== selectedRegion) return;
      if (selectedCountry !== 'all' && site.Country !== selectedCountry) return;
      if (selectedTag !== 'all' && (!site.Tags || !site.Tags.includes(selectedTag))) return;

      Object.values(site.dailyStatus || {}).forEach(status => {
        if (status.BoundingBoxCount) {
          sum += parseInt(status.BoundingBoxCount) || 0;
        }
      });
    });
    return sum;
  }, [auditSummaryWebsites, selectedRegion, selectedCountry, selectedTag]);

  const displayTotalTiles = totalScannedTiles || possibleAnomaliesCount;
  const anomaliesRate = displayTotalTiles > 0 ? (possibleAnomaliesCount / displayTotalTiles) * 100 : 0;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header Title & Date Range */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Anomaly Review</Typography>
          <IconButton
            onClick={() => setWhatsNewOpen(true)}
            size="small"
            color="primary"
            sx={{ ml: 1, border: 1, borderColor: 'primary.light', p: 0.5 }}
            title="What's New Guide"
          >
            <HelpIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => handleShiftDays(-7)}>
            <ArrowBackIcon />
          </IconButton>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={e => handleStartDateChange(e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            inputProps={{ min: startDate }}
          />
          <IconButton onClick={() => handleShiftDays(7)}>
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Possible Anomalies Rate</Typography>
              <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                {anomaliesRate.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={anomaliesRate}
                sx={{ height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">Total Tiles</Typography>
              <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>{displayTotalTiles}</Typography>
              <Typography variant="body2" color="textSecondary">Total game tiles found in the date range</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">Possible Anomalies Found</Typography>
              <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold', color: possibleAnomaliesCount > 0 ? 'error.main' : 'success.main' }}>
                {possibleAnomaliesCount}
              </Typography>
              <Typography variant="body2" color="textSecondary">Awaiting review after filters</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', bgcolor: '#f5f5f5' }}>
        <FilterIcon color="action" />
        <Autocomplete
          options={regions}
          size="small"
          sx={{ width: 180 }}
          value={selectedRegion}
          onChange={(_, val) => { setSelectedRegion(val || 'all'); setSelectedCountry('all'); }}
          renderInput={(params) => <TextField {...params} label="Region" />}
        />
        <Autocomplete
          options={countries}
          size="small"
          sx={{ width: 180 }}
          value={selectedCountry}
          onChange={(_, val) => setSelectedCountry(val || 'all')}
          renderInput={(params) => <TextField {...params} label="Country" />}
        />
        <Autocomplete
          options={availableTags}
          size="small"
          sx={{ width: 180 }}
          value={selectedTag}
          onChange={(_, val) => setSelectedTag(val || 'all')}
          renderInput={(params) => <TextField {...params} label="Tag" />}
        />
      </Paper>

      {/* Main Workspace split */}
      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: '50vh' }}>
            <Typography variant="h6" gutterBottom>Review Lists</Typography>
            <Button variant="outlined" sx={{ ml: 2 }} onClick={() => setManageListsOpen(true)}>
              Manage Lists
            </Button>
            <List sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '400px' }}>
              {lists.map(list => {
                const isSelected = selectedList?.ListID === list.ListID;
                const isSmartStudio = list.Type === 'SmartStudio' || (list.StudioIDs && list.StudioIDs.length > 0);
                const isSmartInhouse = list.Type === 'SmartInhouse';
                const isSmartSuggested = list.Type === 'SmartSuggested';

                let secondaryText = '';
                if (isSelected && listData) {
                  secondaryText = `${listData.Games?.length || 0} games`;
                } else if (isSmartSuggested) {
                  const limit = list.SmartConfig?.TopGamesLimit || 10;
                  secondaryText = `Top ${limit} suggested`;
                } else if (isSmartStudio) {
                  secondaryText = `${list.StudioIDs?.length || 0} studios`;
                } else if (isSmartInhouse) {
                  secondaryText = 'Inhouse logic';
                } else {
                  secondaryText = `${list.Games?.length || 0} games`;
                }

                let listIcon = <ListIcon />;
                if (isSmartSuggested) listIcon = <AutoFixHighIcon color="primary" />;
                else if (isSmartStudio || isSmartInhouse) listIcon = <AutoAwesomeIcon color="secondary" />;

                return (
                  <ListItem
                    button
                    key={list.ListID}
                    selected={isSelected}
                    onClick={() => setSelectedList(list)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {listIcon}
                    </ListItemIcon>
                    <ListItemText
                      primary={list.Name}
                      secondary={secondaryText}
                      primaryTypographyProps={{ fontWeight: isSelected ? 'bold' : 'normal' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2, minHeight: '50vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                {selectedList ? selectedList.Name : 'Select a list to review'}
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && !listData && <CircularProgress />}

            {listData && listData.Games.length === 0 && (
              <Typography color="textSecondary">This list is empty.</Typography>
            )}

            {listData && anomalyGames.length === 0 && (
              <Alert severity="success" sx={{ mt: 4 }}>
                🎉 All clear! No anomalies found for the selected filters.
              </Alert>
            )}

            {listData && anomalyGames.length > 0 && currentAnomalyIndex < anomalyGames.length && (() => {
              const gameId = anomalyGames[currentAnomalyIndex];
              const tiles = filteredTiles[gameId] || [];
              const suggestion = suggestions[gameId];

              return (
                <Fade in={true} key={gameId} timeout={500}>
                  <Card sx={{ mb: 4, mt: 2, boxShadow: 3, borderRadius: 2 }}>
                    {batchLoading && <LinearProgress color="secondary" />}
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
                          Anomaly {currentAnomalyIndex + 1} of {anomalyGames.length}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={batchLoading}
                            onClick={() => {
                              setTilesToReassign(tiles.map(tile => ({ tile, gameId })));
                              setReassignModalOpen(true);
                            }}
                          >
                            Batch Reassign All ({tiles.length})
                          </Button>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<ArrowBackIcon />}
                            disabled={currentAnomalyIndex === 0}
                            onClick={() => setCurrentAnomalyIndex(prev => prev - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            disabled={currentAnomalyIndex >= anomalyGames.length - 1}
                            onClick={() => setCurrentAnomalyIndex(prev => prev + 1)}
                          >
                            Next Anomaly
                          </Button>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                          {gamesMap[gameId] || gameId}
                        </Typography>
                        {(() => {
                          const game = fullGamesMap[gameId];
                          const studio = game ? studiosMap[game.StudioID] : null;
                          if (studio) {
                            const operatorName = studio.OperatorID ? operatorsMap[studio.OperatorID] : null;
                            const isSmartInhouse = selectedList?.ListID === 'smart_inhouse';
                            const subtitle = isSmartInhouse && operatorName
                              ? `${studio.Name}, inhouse studio for ${operatorName}`
                              : studio.Name;

                            return (
                              <Typography variant="subtitle1" color="textSecondary">
                                {subtitle}
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>

                      {suggestion && (
                        <Alert severity="info" sx={{ mb: 3 }} action={
                          <Button color="inherit" size="small" onClick={() => handleApplyCorrection(gameId, suggestion.GameID, tiles)}>
                            Apply Correction
                          </Button>
                        }>
                          Smart Suggestion: Change to <b>{gamesMap[suggestion.GameID] || suggestion.GameID}</b> (Suggested {suggestion.Count} times)
                        </Alert>
                      )}

                      <Divider sx={{ mb: 3 }} />

                      <Box ref={setCarouselRef} sx={{ display: 'flex', overflowX: 'auto', gap: 3, pb: 2 }}>
                        {tiles.map(tile => {
                          const meta = tileMetadata[tile.WebsitesDataID];

                          return (
                            <Box key={`${tile.WebsitesDataID}-${tile.BoundingBoxID}`} sx={{ minWidth: 220, maxWidth: 220, display: 'flex', flexDirection: 'column', p: 1, border: '1px solid #f0f0f0', borderRadius: 2, bgcolor: '#fafafa' }}>
                              <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, bgcolor: 'white', borderRadius: 1 }}>
                                <SightingCropImage
                                  WebsitesDataID={tile.WebsitesDataID}
                                  BoundingBoxID={tile.BoundingBoxID}
                                  BoundingBox={tile.BoundingBox}
                                  onLoad={(url, metadata) => {
                                    if (metadata && !tileMetadata[tile.WebsitesDataID]) {
                                      setTileMetadata(prev => ({ ...prev, [tile.WebsitesDataID]: metadata }));
                                    }
                                  }}
                                />
                              </Box>
                              {meta ? (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }} noWrap>{meta.WebsiteName}</Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1.5, display: 'block' }}>
                                    {new Date(meta.Timestamp).toLocaleString()}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      disableElevation
                                      fullWidth
                                      sx={{ fontSize: '0.7rem' }}
                                      onClick={() => window.open(`/time-machine?site=${meta.WebsitesDBID}&scanId=${tile.WebsitesDataID}&date=${meta.Timestamp}`, '_blank')}
                                    >
                                      Time Machine
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      sx={{ fontSize: '0.7rem', flex: 1, minWidth: 0 }}
                                      onClick={() => {
                                        setTilesToReassign([{ tile, gameId }]);
                                        setReassignModalOpen(true);
                                      }}
                                    >
                                      Reassign
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      sx={{ fontSize: '0.7rem', flex: 1, minWidth: 0 }}
                                      onClick={() => handleDeleteTile(tile, gameId)}
                                    >
                                      Delete
                                    </Button>
                                  </Box>
                                </>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                  <CircularProgress size={20} />
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>

      {/* Create List Modal */}
      <ManageListsModal
        open={manageListsOpen}
        onClose={() => setManageListsOpen(false)}
        lists={lists}
        refreshLists={fetchLists}
        gamesMap={gamesMap}
        studiosMap={studiosCatalog.reduce((acc, s) => ({ ...acc, [s.StudioID]: s.Name }), {})}
      />

      {/* Reassign Modal */}
      <Dialog open={reassignModalOpen} onClose={() => { setReassignModalOpen(false); setTilesToReassign([]); setSelectedNewGame(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign Occurrence</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Autocomplete
            options={Object.entries(gamesMap).map(([id, name]) => ({ id, name }))}
            getOptionLabel={(option) => option.name}
            value={selectedNewGame}
            onChange={(event, newValue) => setSelectedNewGame(newValue)}
            renderInput={(params) => <TextField {...params} label="Search for new game..." variant="outlined" autoFocus />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignModalOpen(false)}>Cancel</Button>
          <Button onClick={handleReassignTile} variant="contained" disabled={!selectedNewGame}>Confirm Reassign</Button>
        </DialogActions>
      </Dialog>

      {/* What's New Dialog */}
      <Dialog
        open={whatsNewOpen}
        onClose={handleCloseWhatsNew}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: 24, p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>✨</span> {whatsNewLang === 'en' ? "What's New in Anomaly Review!" : "Novidades na Revisão de Anomalias!"}
          </Typography>
          <ToggleButtonGroup
            value={whatsNewLang}
            exclusive
            onChange={(e, val) => val && setWhatsNewLang(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': { py: 0.2, px: 1.5, fontSize: '1.2rem', borderRadius: '8px' }
            }}
          >
            <ToggleButton value="en" title="English">🇬🇧</ToggleButton>
            <ToggleButton value="pt" title="Português (Brasil)">🇧🇷</ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {whatsNewLang === 'en'
              ? "Welcome to the new Anomaly Review page! Here are some of the main features you can use to clean up data:"
              : "Bem-vindo à nova página de Revisão de Anomalias! Aqui estão alguns dos principais recursos que você pode usar para limpar dados:"}
          </Typography>

          <Stack spacing={2.5}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(25, 118, 210, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                🎯
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {whatsNewLang === 'en' ? "List-Based Workflow" : "Fluxo de Trabalho Baseado em Listas"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {whatsNewLang === 'en'
                    ? "Create and manage Review Lists to isolate specific games you suspect are misidentified by the AI."
                    : "Crie e gerencie Listas de Revisão para isolar jogos específicos que você suspeita estarem incorretos pela IA."}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(156, 39, 176, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                🤖
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {whatsNewLang === 'en' ? "Smart Suggestions" : "Sugestões Inteligentes"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {whatsNewLang === 'en'
                    ? "Some lists are Smart Lists - they will provide an automatic AI recommendation to help you quickly reassign games."
                    : "Algumas listas são Listas Inteligentes - elas fornecem uma recomendação automática da IA para ajudar você a reatribuir jogos rapidamente."}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(76, 175, 80, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                ⚡
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {whatsNewLang === 'en' ? "Batch Reassignments" : "Reatribuições em Lote"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {whatsNewLang === 'en'
                    ? "Review all occurrences of an anomaly at once and reassign them all to the correct game with a single click."
                    : "Revise todas as ocorrências de uma anomalia de uma só vez e reatribua todas ao jogo correto com um único clique."}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleDontShowWhatsNew} sx={{ textTransform: 'none', color: '#666' }}>
            {whatsNewLang === 'en' ? "Exit, and Don't Show Again" : "Sair e Não Mostrar Novamente"}
          </Button>
          <Button variant="contained" onClick={handleCloseWhatsNew} sx={{ px: 3, borderRadius: 2 }}>
            {whatsNewLang === 'en' ? "Exit" : "Sair"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withAuthenticator(AnomalyReview);
